import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Center,
  Badge,
  Group,
  Box,
  TextInput,
  Skeleton,
  ActionIcon,
  ScrollArea,
  Loader,
  Button,
} from '@mantine/core';
import {
  IconBook,
  IconSearch,
  IconX,
  IconArrowDown,
  IconPlus,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useLibrary } from '@/features/library/hooks/useLibrary';
import { getThemeConfig } from '@/config/themes';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { QuickReadButton } from '@/features/recommendations';

interface StoryLibraryItem {
  id: string;
  title: string;
  theme_id: string;
  passage_excerpt?: string | null;
  word_count?: number | null;
  created_at: string;
  best_score?: number | null;
  quiz_attempted?: boolean;
}

/**
 * Mobile Library Page
 *
 * Simplified card-based library for mobile:
 * - Vertical scrollable story cards
 * - Touch-optimized tap targets
 * - Simple search (no complex filters)
 * - Pull-to-refresh ready
 */
export function MobileLibraryPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isStudent = user?.roles?.includes('student');
  const queryClient = useQueryClient();

  const { data: stories, isLoading, refetch } = useLibrary(isStudent ? 'history' : 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Filter stories by search query
  const filteredStories = stories?.filter((story: StoryLibraryItem) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      story.title.toLowerCase().includes(query) ||
      getThemeConfig(story.theme_id).label.toLowerCase().includes(query)
    );
  });

  const handleStoryClick = (storyId: string) => {
    navigate(`/story/${storyId}`);
  };

  // Pull-to-refresh using native event listeners for proper passive: false support
  useEffect(() => {
    const scrollAreaElement = scrollAreaRef.current;
    if (!scrollAreaElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      const viewport = scrollAreaElement.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      const scrollTop = viewport?.scrollTop ?? 0;

      if (scrollTop <= 1) {
        touchStartY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === 0) return;

      const viewport = scrollAreaElement.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      const scrollTop = viewport?.scrollTop ?? 0;

      if (scrollTop <= 1) {
        const currentY = e.touches[0].clientY;
        const distance = Math.max(0, currentY - touchStartY.current);

        if (distance > 0) {
          e.preventDefault();
          setPullDistance(Math.min(distance, 80));
        }
      }
    };

    const handleTouchEnd = async () => {
      const currentPullDistance = pullDistanceRef.current;
      if (currentPullDistance > 60) {
        setIsRefreshing(true);
        await refetch();
        queryClient.invalidateQueries({ queryKey: ['library'] });
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 500);
      } else {
        setPullDistance(0);
      }
      touchStartY.current = 0;
    };

    scrollAreaElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollAreaElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollAreaElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      scrollAreaElement.removeEventListener('touchstart', handleTouchStart);
      scrollAreaElement.removeEventListener('touchmove', handleTouchMove);
      scrollAreaElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [refetch, queryClient]);

  // Keep a ref to pullDistance for the native event handler
  const pullDistanceRef = useRef(pullDistance);
  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  if (isLoading) {
    return (
      <Container size="sm" py="md">
        <Stack gap="md">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={140} radius="md" />
          ))}
        </Stack>
      </Container>
    );
  }

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Search */}
      <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Title order={2}>
              {isStudent ? 'My Stories' : 'Story Library'}
            </Title>
            <Button
              leftSection={<IconPlus size={18} />}
              variant="light"
              size="sm"
              onClick={() => navigate('/library/story/create')}
            >
              New Story
            </Button>
          </Group>

          {/* Quick Read - prominent call to action for students */}
          {isStudent && (
            <QuickReadButton size="md" fullWidth />
          )}

          <TextInput
            placeholder="Search stories..."
            leftSection={<IconSearch size={16} />}
            rightSection={
              searchQuery ? (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setSearchQuery('')}
                >
                  <IconX size={16} />
                </ActionIcon>
              ) : null
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
          />
        </Stack>
      </Box>

      {/* Story List */}
      <ScrollArea
        ref={scrollAreaRef}
        style={{ flex: 1 }}
        offsetScrollbars
      >
        {/* Pull-to-refresh indicator */}
        <Box
          style={{
            height: `${pullDistance}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: pullDistance === 0 ? 'height 0.3s ease-out' : 'none',
            overflow: 'hidden',
          }}
        >
          {isRefreshing ? (
            <Loader size="sm" />
          ) : pullDistance > 0 && (
            <IconArrowDown
              size={20}
              style={{
                opacity: pullDistance / 80,
                transform: `rotate(${pullDistance > 60 ? 180 : 0}deg)`,
                transition: 'transform 0.2s',
              }}
            />
          )}
        </Box>

        <Container size="sm" py="md">
          {filteredStories && filteredStories.length > 0 ? (
            <Stack gap="md">
              {filteredStories.map((story: StoryLibraryItem) => {
                // Use story.theme object if available (has slug), otherwise try to get config by theme_id
                const themeSlug = (story as any).theme?.slug || story.theme_id;
                const theme = getThemeConfig(themeSlug);
                // Check both has_completed (from enriched data) and quiz_attempted (from API)
                const hasCompleted = (story as any).has_completed || story.quiz_attempted;
                const score = story.best_score;

                // Only show theme label if it's not a UUID (UUIDs have dashes and are 36 chars)
                const showThemeLabel = theme.label && !story.theme_id.includes('-');

                return (
                  <Card
                    key={story.id}
                    shadow="sm"
                    padding="lg"
                    radius="md"
                    withBorder
                    onClick={() => handleStoryClick(story.id)}
                    style={{ cursor: 'pointer', minHeight: '120px' }}
                  >
                    <Stack gap="xs">
                      {/* Title and Theme */}
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text fw={600} size="lg" lineClamp={2}>
                            {story.title}
                          </Text>
                          {showThemeLabel && (
                            <Text size="sm" c="dimmed" mt={4}>
                              {theme.label}
                            </Text>
                          )}
                        </Box>
                        <IconBook size={24} color={theme.color} style={{ flexShrink: 0 }} />
                      </Group>

                      {/* Excerpt */}
                      {story.passage_excerpt && (
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {story.passage_excerpt}
                        </Text>
                      )}

                      {/* Footer with metadata */}
                      <Group justify="space-between" mt="xs">
                        <Group gap="xs">
                          {story.word_count && (
                            <Badge variant="light" color="gray" size="sm">
                              {story.word_count} words
                            </Badge>
                          )}
                          {hasCompleted && (
                            <Badge
                              variant="light"
                              color={score && score >= 80 ? 'green' : 'blue'}
                              size="sm"
                            >
                              {score ? `${score}%` : 'Completed'}
                            </Badge>
                          )}
                        </Group>
                        {!hasCompleted && (
                          <Badge variant="light" color="orange" size="sm">
                            Not started
                          </Badge>
                        )}
                      </Group>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Center py="xl">
              <Stack align="center" gap="md">
                <IconBook size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
                <Stack align="center" gap="xs">
                  <Text size="lg" fw={500} c="dimmed">
                    {searchQuery ? 'No stories found' : 'No stories yet'}
                  </Text>
                  {!searchQuery && (
                    <Text size="sm" c="dimmed" ta="center">
                      {isStudent
                        ? 'Start reading to build your library'
                        : 'Generate stories to get started'}
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Center>
          )}
        </Container>
      </ScrollArea>
    </Box>
  );
}
